import React, { useEffect, useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from '@ag-grid-community/react';
import './distributor-configuration-table.scss';
import {
  distributorConfigurationTableAutoGroupColumnDef,
  distributorConfigurationTableColumnDefs,
  distributorsIPsWhiteListEditableTableColumnDefs,
  distributorsIPsWhiteListReadOnlyTableColumnDefs,
} from 'routes/AccessFunction/AccessFunction.types';
import {
  DistributorConfigurationListenerAfWhiteList,
  DistributorConfigurationTableData,
} from 'data/distributorConfiguration/distributorConfiguration.types';
import { FormTemplateSection } from 'data/template/template.types';
import { TranslationKey } from 'i18n/i18n.types';
import isTestEnvironment from 'utils/environment/isTestEnvironment';
import { GetLocaleTextParams } from 'aggrid/agGrid.types';
import EditAllowedIPsDialog from '../../../../components/EditAllowedIPsDialog';
import * as utils from './DistributorConfigurationTable.utils';

type Props = {
  onDistributorUpdate?: (distributorConfiguration: DistributorConfigurationTableData) => void,
  isEditing: boolean,
  rowData: DistributorConfigurationTableData[],
  templateSection: FormTemplateSection,
}

const DistributorConfigurationTable: FC<Props> = ({
  rowData,
  templateSection,
  isEditing,
  onDistributorUpdate,
}: Props) => {
  const [selectedDistributor, setSelectedDistributor] = useState<DistributorConfigurationTableData>();
  const [selectedDistributorId, setSelectedDistributorId] = useState<string>('');
  const { t } = useTranslation();

  const adaptedDistributorConfigurationTableColDefs = utils.adaptColumnDefsBasedInTemplate(
    distributorConfigurationTableColumnDefs,
    templateSection,
  );

  useEffect(() => {
    if (selectedDistributorId) {
      setSelectedDistributor(utils.findDistributorById(rowData, selectedDistributorId));
    }
  }, [selectedDistributorId, rowData]);

  const handleSelectAfIp = (params: DistributorConfigurationTableData) => {
    setSelectedDistributorId(params.id);
  };

  const handleUpdateIpList = (newAFIpsList: DistributorConfigurationListenerAfWhiteList[]) => {
    if (!selectedDistributor || !onDistributorUpdate) { return; }

    onDistributorUpdate(utils.updateDistributorWhiteListIps(selectedDistributor, newAFIpsList));

    setSelectedDistributor(undefined);
    setSelectedDistributorId('');
  };

  const handleCloseEditIPsDialog = () => {
    setSelectedDistributor(undefined);
    setSelectedDistributorId('');
  };

  const handleGridReady = (event: any) => {
    if (isTestEnvironment()) {
      // Prevent sizeColumnsToFit to be called in a test environment.
      // This is not required in a test environment and will also prevent a
      // warning log in the test output.
      return;
    }

    event.api.sizeColumnsToFit();
  };

  let ipTableColumnDefs = distributorsIPsWhiteListReadOnlyTableColumnDefs;
  if (isEditing) {
    ipTableColumnDefs = distributorsIPsWhiteListEditableTableColumnDefs;
  }

  const localeText = ({ key, defaultValue }: GetLocaleTextParams): string => {
    return t(`agGrid.${key}` as TranslationKey, defaultValue);
  };

  return (
    <div className="ag-theme-balham distributor-configuration-table-theme">
      <AgGridReact
        onGridReady={handleGridReady}
        groupHideOpenParents
        groupDefaultExpanded={1}
        autoGroupColumnDef={distributorConfigurationTableAutoGroupColumnDef}
        columnDefs={adaptedDistributorConfigurationTableColDefs}
        rowData={rowData}
        gridOptions={{
          context: {
            onSelectAfIp: handleSelectAfIp,
          },
        }}
        getLocaleText={localeText}
      />
      <EditAllowedIPsDialog
        isOpen={!!selectedDistributor}
        ipsWhiteList={selectedDistributor?.listener?.afWhitelist || []}
        isEditing={isEditing}
        ipTableColumnDefs={ipTableColumnDefs}
        distributorName={selectedDistributor?.name}
        listeningIpAddress={selectedDistributor?.ipAddress}
        listeningPort={selectedDistributor?.port}
        onSubmit={handleUpdateIpList}
        onCancel={handleCloseEditIPsDialog}
      />
    </div>
  );
};

export default DistributorConfigurationTable;
